/**
 *
 * Publications Page
 *
 */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { createStructuredSelector } from 'reselect';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import cloneDeep from 'lodash/cloneDeep';

import { PageWrapper, PageViewport } from 'components/PageStyles';
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import { pushToast } from 'containers/ToastManager/actions';
import helpContext from 'constants/helpContext';
import { commandWorkspacePublicationsList } from 'containers/ConnectionManager/actions';
import Sidebar from 'Workspace/components/WorkspaceSidebar';
import HomeNavigation from 'Workspace/components/HomeNavigation';
import WorkspaceHeader from 'Workspace/components/WorkspaceHeader';
import CourseSelectorModal from 'Workspace/components/CourseSelectorModal';
import HelpMenu from 'Workspace/components/WorkspaceHelpMenu';
import workspaceService from 'services/WorkspaceService';
import gtmService from 'utils/gtm/gtmService';
import { GTM_EVENT_TYPES } from 'utils/gtm/constants';
import publicationService from 'services/PublicationService';
import { featureKeys } from 'services/FeatureFlagService';
import { setPublicationsList } from '../../store/actions';
import { selectPublicationsList } from '../../store/selectors';
import { publicationsModel, publishFormat } from '../../utils/constants';
import PublishPostActivitySelectFlow from '../PublicationModal/PublishPostActivitySelectFlow';
import PublicationPageContent from './PublicationPageContent';
import messages from './messages';
import {
  HeaderWrapper,
  HeaderBackground,
  HeaderTitle,
  HeaderBackgroundContainer,
} from './styles';
import TabHeader from 'components/TabHeader';
import enhanceWithFeatureFlagSubscription from 'hocs/enhanceWithFeatureFlagSubscription';

/**
 * Renders Publication Page
 */
const PublicationPage = ({ setPublicationList, publicationList, displayToast, getPublicationsList, getFeatureFlagValue }) => {

  const [telemetry, setTelemetry] = useState(null);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const displayProjectList = getFeatureFlagValue(featureKeys.WORKSPACE_PUBLICATION_ADDMODAL_DISPLAYPROJECTLIST);
  const isTabHeaderVisible = getFeatureFlagValue(featureKeys.BRNT_18710_WORKSPACE_REDESIGN_22);
  const [outputType, setOutputType] = useState(publishFormat.EPUB);
  const [market, setMarket] = useState(null);
  const selectedWorkspace = workspaceService.getSelectedWorkspace();

  const onNextCourseware = (activity) => {
    setSelectedActivity(activity);
  };

  /**
   * Show Course selector modal
   */
  const showPublicationSelectorModal = (event) => {
    event.stopPropagation();
    event.preventDefault();
    setMarket(null);
    setVisible(true);
  };

  const addPublicationItem = (data) => ({ ...publicationsModel, ...data, unPublishedInstance: true });

  /**
   * Adds publication list either to top level or as a version within the dropdown
   * @param {object} newPubItem
   * @param {boolean} version
   */
  const addToPublications = (newPublicationItem) => {
    const publicationListClone = cloneDeep(publicationList);
    const isPublicationAdded = publicationService.checkIfPublicationItemAdded(publicationListClone, newPublicationItem);
    if (isPublicationAdded) {
      displayToast({ message: intl.formatMessage(messages.coursePresent) });
    } else {
      setPublicationList([addPublicationItem(newPublicationItem), ...publicationListClone]);
    }
  };

  const setPublicationOutputType = (outputType) => {
    setOutputType(outputType);
  };

  const setPublicationMarket = (market) => {
    setMarket(market);
  };
  /**
   * Callback from modal publish confirmation
   * Dismiss modal after the pub item added to list
   */
  const onAddToPublication = ({ activity, author, title }) => {
    const newPublicationItem = {
      activityId: activity?.activityId,
      title,
      author,
      outputType,
      market,
    };
    addToPublications(newPublicationItem);
    onDismiss();
  };

  /**
   * Dismiss course selector modal
   */
  const onDismiss = () => {
    setVisible(false);
  };

  useEffect(() => {
    getPublicationsList();
  }, [getPublicationsList]);

  useEffect(() => {
    if (publicationList) {
      setLoading(false);
    }
  }, [publicationList]);

  /**
   * Telemetry init
   */
  useEffect(() => {
    (async () => {
      if (location && telemetry === null) {
        const { pathname } = location;
        gtmService.gtmTrackEvent({ eventType: GTM_EVENT_TYPES.PAGEVIEW, payload: { pageName: pathname } });
        setTelemetry(true);
      }
    })();
  }, [telemetry]);

  return (
    <PageWrapper>
      <Helmet>
        <title>{intl.formatMessage(messages.publications)}</title>
      </Helmet>
      <Sidebar navigation={<HomeNavigation selectedKey="publications" />} />
      <PageViewport style={{ position: 'relative' }}>
        <HeaderWrapper>
          {publicationList?.length ?
            <HeaderBackground as={!isTabHeaderVisible && HeaderBackgroundContainer}>
              {isTabHeaderVisible ?
                <WorkspaceHeader title={selectedWorkspace && selectedWorkspace.name} />
                : <><WorkspaceHeader /><HeaderTitle>{intl.formatMessage(messages.publications)}</HeaderTitle></>}
              {isTabHeaderVisible && <TabHeader></TabHeader>}
            </HeaderBackground> : <WorkspaceHeader title={intl.formatMessage(messages.publications)} />}
        </HeaderWrapper>
        <CourseSelectorModal
          onPrimaryHandler={onNextCourseware}
          onDismiss={onDismiss}
          visible={visible}
          primaryButtonText={intl.formatMessage(messages.selectButton)}
          showSelectedWorkspace={displayProjectList}
          postActivitySelectView={
            <PublishPostActivitySelectFlow
              onDismiss={onDismiss}
              setPublicationOutputType={setPublicationOutputType}
              setPublicationMarket={setPublicationMarket}
              outputType={outputType}
              addToPublication={onAddToPublication}
              activity={selectedActivity}
            />}
        />
        <PublicationPageContent
          loading={loading}
          publicationList={publicationList}
          showPublicationSelectorModal={showPublicationSelectorModal}
        />
      </PageViewport>
      <HelpMenu helpContext={helpContext.KNOWLEDGE} />
    </PageWrapper>
  );
};

const mapStateToProps = createStructuredSelector({
  publicationList: selectPublicationsList,
});

const mapDispatchToProps = (dispatch) => ({
  getPublicationsList: () => dispatch(commandWorkspacePublicationsList()),
  setPublicationList: (publications) => dispatch(setPublicationsList(publications)),
  displayToast: (message) => dispatch(pushToast(message)),
});

PublicationPage.propTypes = {
  getPublicationsList: PropTypes.func,
  setPublicationList: PropTypes.func,
  publicationList: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  displayToast: PropTypes.func,
};

const flagNames = [featureKeys.WORKSPACE_PUBLICATION_ADDMODAL_DISPLAYPROJECTLIST, featureKeys.BRNT_18710_WORKSPACE_REDESIGN_22];

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect, enhanceWithFeatureFlagSubscription)(PublicationPage, flagNames);
